import React, {useEffect, useState} from "react";
import PageTitle from "../../../components/page_title/page_title";
import styles from "../../../styles/dashboard.module.css";
import mystyles from './history.module.css';
import Header from "../../../components/header/header";
import axiosInstance from "../../../AxiosInstance";
import {formatTimestamp} from "../../../components/utils";
import Modal from "../../../components/modal/modal";
import ReactFlow, {Background} from "reactflow";
import OperationNode from "../../../nodes/OperationNode";
import ValueNode from "../../../nodes/ValueNode";
import ActionNode from "../../../nodes/ActionNode";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEnvelope, faChartLine, faComment, faQuestion, faCommentDots, faNetworkWired, faCode, faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

function UserActivity({data}) {
    const {t} = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [currentNodes, setCurrentNodes] = useState([]);
    const [currentEdges, setCurrentEdges] = useState([]);
    const nodeTypes = { operation: OperationNode, value: ValueNode, action: ActionNode };

    return (
        <div className={`${mystyles.historyContainer} mb-4`}>
            <Modal show={showModal} setter={setShowModal} onClick={() => setShowModal(false)} hideCancel={true} buttonName={t('common.close')}>
                <div className={'d-flex flex-row'} style={{height: '400px'}}>
                    <ReactFlow
                        nodeTypes={nodeTypes}
                        nodes={currentNodes}
                        edges={currentEdges}
                    >
                        <Background variant={"dots"} gap={12} size={1}/>
                    </ReactFlow>
                </div>
            </Modal>
            <div>{t('operationsHistory.userActivity')}</div>
            {data.map((modification, index) => (
                <div key={index}>
                    <div className={'d-flex align-items-center justify-content-between'}>
                        <div className={'d-flex align-items-center'}>
                            <div className={'d-flex flex-column justify-content-center align-items-center me-2'}>
                                <div className={mystyles.continuation}/>
                            </div>
                            <div>
                                <div className={mystyles.actionName}>{modification.user.first_name + " " + modification.user.last_name} ({modification.user.email})</div>
                                <div className={mystyles.timestamp}>{formatTimestamp(modification.timestamp)}</div>
                            </div>
                        </div>
                        <div>
                            <div
                                onClick={() => {
                                    setShowModal(true);
                                    setCurrentEdges(modification.edges);
                                    setCurrentNodes(modification.nodes);
                                }}
                                style={{
                                backgroundColor: '#E0E7FF',
                                color: '#4F46E5',
                                fontWeight: '600',
                                padding: '6px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}>{t('operationsHistory.show')}</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ActionSummary({action}) {
    const {t} = useTranslation();
    switch (action.type) {
        case 1: // Email
            return (
                <div className={mystyles.actionSummary}>
                    {action.data_1 && <span>{t('operationsHistory.to')}: {action.data_1}</span>}
                    {action.data_2 && <span> · {t('operationsHistory.subject')}: {action.data_2}</span>}
                </div>
            );
        case 2: // Dashboard
            return (
                <div className={mystyles.actionSummary}>
                    {action.data_3 && <span>{t('operationsHistory.widget')}: {action.data_3}</span>}
                    {action.data_2 && <span> · {t('operationsHistory.value')}: {action.data_2}</span>}
                </div>
            );
        case 3: // SMS
            return (
                <div className={mystyles.actionSummary}>
                    {action.data_2 && <span>{t('operationsHistory.to')}: {action.data_2}</span>}
                </div>
            );
        case 4: // WhatsApp
            return (
                <div className={mystyles.actionSummary}>
                    {action.data_2 && <span>{t('operationsHistory.to')}: {action.data_2}</span>}
                </div>
            );
        case 5: // MQTT
            return (
                <div className={mystyles.actionSummary}>
                    {action.data_1 && <span>{t('operationsHistory.topic')}: {action.data_1}</span>}
                </div>
            );
        case 6: // API Call
            return (
                <div className={mystyles.actionSummary}>
                    {action.data_1 && <span>{action.data_1}</span>}
                    {action.data_2 && <span> {action.data_2}</span>}
                </div>
            );
        default:
            return null;
    }
}

function ActionDetails({action}) {
    const {t} = useTranslation();
    const getDetailLabel = (type) => {
        switch (type) {
            case 1: return t('operationsHistory.body');
            case 3: case 4: return t('operationsHistory.message');
            case 5: return t('operationsHistory.payload');
            case 6: return t('operationsHistory.body');
            default: return t('operationsHistory.content');
        }
    };

    if (!action.data_3 || action.type === 2) return null;

    return (
        <div className={mystyles.actionDetailContent}>
            <span className={mystyles.actionDetailLabel}>{getDetailLabel(action.type)}:</span>
            <div className={mystyles.actionDetailValue}>{action.data_3}</div>
        </div>
    );
}

function ActionHistory({data}) {
    const {t} = useTranslation();
    const [expandedIndex, setExpandedIndex] = useState(null);

    const typeToString = (type) => {
        switch (type) {
            case 1: return t('operationsHistory.actionTypes.email');
            case 2: return t('operationsHistory.actionTypes.dashboard');
            case 3: return t('operationsHistory.actionTypes.sms');
            case 4: return t('operationsHistory.actionTypes.whatsapp');
            case 5: return t('operationsHistory.actionTypes.mqtt');
            case 6: return t('operationsHistory.actionTypes.api');
            default: return t('operationsHistory.actionTypes.unknown');
        }
    }

    const typeToIcon = (type) => {
        switch (type) {
            case 1: return faEnvelope;
            case 2: return faChartLine;
            case 3: return faComment;
            case 4: return faCommentDots;
            case 5: return faNetworkWired;
            case 6: return faCode;
            default: return faQuestion;
        }
    }

    const hasDetails = (action) => action.data_3 && action.type !== 2;

    return (
        <div className={`${mystyles.historyContainer} mb-4`}>
            <div>{t('operationsHistory.executedActions')}</div>
            {data.map((action, index) => (
                <div key={index}>
                    <div className={'d-flex align-items-start'}>
                        <div className={'d-flex flex-column justify-content-center align-items-center me-2'} style={{paddingTop: '2px'}}>
                            <div style={{
                                backgroundColor: '#EEF2FF',
                                borderRadius: '8px',
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FontAwesomeIcon icon={typeToIcon(action.type)} color="#4F6BED" size="lg"/>
                            </div>
                            <div className={mystyles.continuation}/>
                        </div>
                        <div style={{flex: 1, minWidth: 0}}>
                            <div className={mystyles.actionName}>{typeToString(action.type)}</div>
                            <ActionSummary action={action}/>
                            {hasDetails(action) && (
                                <div
                                    className={mystyles.detailToggle}
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                >
                                    <FontAwesomeIcon
                                        icon={expandedIndex === index ? faChevronUp : faChevronDown}
                                        size="xs"
                                        style={{marginRight: '4px'}}
                                    />
                                    {expandedIndex === index ? t('operationsHistory.hide') : t('operationsHistory.details')}
                                </div>
                            )}
                            {expandedIndex === index && <ActionDetails action={action}/>}
                            <div className={mystyles.timestamp}>{formatTimestamp(action.timestamp)}</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function OperationsHistory() {
    const {t} = useTranslation();
    const [actionHistory, setActionHistory] = useState([]);
    const [operationHistory, setOperationHistory] = useState([]);
    const operation_group_id = parseInt(localStorage.getItem('operation_group_id'));

    useEffect(() => {
        if (operation_group_id) {
            axiosInstance.get(`action_history/?operation_group_id=${operation_group_id}`).then((response) => {
                setActionHistory(response.data);
            });

            axiosInstance.get(`operation_history/?group=${operation_group_id}`).then((response) => {
                setOperationHistory(response.data);
            });
        }
    }, []);

    return (
        <div className={styles.dashboard_container}>
            <Header/>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('operationsHistory.title')} back={'/operations'}/>

                <div className={`${mystyles.container} mt-3`}>
                    <ActionHistory data={actionHistory}/>
                    <UserActivity data={operationHistory}/>
                </div>
            </div>
        </div>
    );
}